import type { TripSectionId } from "./TripsPage";

/** This is an editable itinerary-planning workflow, NOT a personal checklist. */
export const guidedStepKinds = ["accommodation", "flight", "train", "transfer", "documents", "activity", "insurance", "other"] as const;
export type GuidedStepKind = typeof guidedStepKinds[number];
export type GuidedStepStatus = "todo" | "done" | "skipped";
export interface GuidedStep {
  id: string;
  kind: GuidedStepKind;
  title: string;
  status: GuidedStepStatus;
  createdAt: string;
  date?: string;
}
export type GuidedPlan = GuidedStep[];

export const guidedStepSections: Record<GuidedStepKind, TripSectionId> = {
  accommodation: "hotels", flight: "boardingPass", train: "itinerary", transfer: "itinerary",
  documents: "documents", activity: "experiences", insurance: "documents", other: "notes"
};

/** Starts with common, independent activities; none is mandatory or auto-confirmed. */
export const initialGuidedStepKinds = ["accommodation", "flight", "documents", "activity", "transfer"] as const satisfies readonly GuidedStepKind[];

export function normalizeGuidedPlan(value: unknown): GuidedPlan | null {
  if (!Array.isArray(value)) return null;
  const ids = new Set<string>();
  return value.slice(0, 200).flatMap((raw): GuidedStep[] => {
    if (!raw || typeof raw !== "object") return [];
    const item = raw as Partial<GuidedStep>;
    if (typeof item.id !== "string" || !/^[a-zA-Z0-9_-]{3,100}$/.test(item.id) || ids.has(item.id)) return [];
    if (!guidedStepKinds.includes(item.kind as GuidedStepKind) || typeof item.title !== "string" || !item.title.trim()) return [];
    if (item.status !== "todo" && item.status !== "done" && item.status !== "skipped") return [];
    ids.add(item.id);
    return [{ id: item.id, kind: item.kind as GuidedStepKind, title: item.title.trim().slice(0, 120),
      status: item.status, createdAt: typeof item.createdAt === "string" ? item.createdAt : "",
      ...(typeof item.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(item.date) ? { date: item.date } : {}) }];
  });
}

export function nextGuidedStep(steps: GuidedPlan): GuidedStep | null {
  return steps.find(step => step.status === "todo") ?? null;
}

export function moveGuidedStep(steps: GuidedPlan, id: string, delta: -1 | 1): GuidedPlan {
  const from = steps.findIndex(step => step.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= steps.length) return steps;
  const next = [...steps];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}
